import api from "../services/api";
import { APIS } from "../endpoints";

/**
 * This function return songs data
 * @returns {Promise<Object>} - Function return Songs data
 */

export const getPersonalityData = async (limit, page, searchTerm, filterOptions = {}) => {
    const response = await getPersonalityAPIData(limit, page, searchTerm, filterOptions);
    return response;
};

const _isDefaultFilterOptions = (opts) => {
    if (!opts || typeof opts !== 'object') return true;
    const filters = opts.filters;
    const hasFilterValues = filters && typeof filters === 'object'
        && Object.values(filters).some((v) => Array.isArray(v) ? v.length > 0 : Boolean(v));
    if (hasFilterValues) return false;
    if (opts.search) return false;
    return true;
};

export const getPersonalityAPIData = async (limit, page, searchTerm, filterOptions = {}) => {
    const formData = new FormData();
    formData.append('page', page);
    formData.append('limit', limit);

    if (!_isDefaultFilterOptions(filterOptions)) {
        const cleanedOptions = {
            ...filterOptions,
            filters: Object.fromEntries(
                Object.entries(filterOptions.filters || {}).filter(([, v]) =>
                    Array.isArray(v) ? v.length > 0 : Boolean(v)
                )
            ),
        };
        formData.append('filters', JSON.stringify(cleanedOptions));
    }

    if (searchTerm) {
        formData.append('search', searchTerm);
    }

    if (filterOptions.sortBy_v) {
        formData.append('sort_by', filterOptions.sortBy_v);
    }

    if (filterOptions.sortOrder_v) {
        formData.append('sort_order', filterOptions.sortOrder_v);
    }

    const params = new URLSearchParams(formData);

    const result = await api.get(`${APIS.BASE_URL}${APIS.adminPersonality}`, {
        params: params
    })
    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const getPersonalityBasicData = async (alias) => {

    const result = await api.get(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.filmBasic}`)
    if (result) {
        return result?.data?.data;
    } else {
        return [];
    }
};

export const updatePersonalityBasicData = async (alias, data, deletedValues) => {

    const payload = {
        ...data,
        deletedValues: deletedValues
    };

    const result = await api.put(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.filmBasic}`, payload)

    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const getPersonalityLandingPageData = async (alias) => {

    const result = await api.get(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.filmLandingPage}`)
    if (result) {
        return result?.data?.data;
    } else {
        return [];
    }
};

export const updatePersonalityLandingPageData = async (alias, data) => {

    const result = await api.put(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.filmLandingPage}`, data)

    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const deletePersonalityLandingPageData = async (alias, deletedItems) => {

    const result = await api.delete(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.filmLandingPage}`, {data: { deletedItems }})

    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const getPersonalityPrefaceData = async (alias) => {

    const result = await api.get(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.filmPreface}`)
    if (result) {
        return result?.data?.data;
    } else {
        return [];
    }
};

export const updatePersonalityPrefaceData = async (alias, data) => {
    const result = await api.put(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.filmPreface}`, data)

    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const getPersonalityPersonaliaData = async (alias) => {

    const result = await api.get(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.personalia}`)
    if (result) {
        return result?.data?.data;
    } else {
        return [];
    }
};

export const updatePersonalityPersonaliaData = async (alias, data) => {
    const result = await api.put(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.personalia}`, data)

    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const getPersonalityEducationData = async (alias) => {

    const result = await api.get(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.education}`)
    if (result) {
        return result?.data?.data;
    } else {
        return [];
    }
};

export const updatePersonalityEducationData = async (alias, data) => {
    const result = await api.put(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.education}`, data)

    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const getPersonalityTeachingData = async (alias) => {

    const result = await api.get(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.teachingInfrastructure}`)
    if (result) {
        return result?.data?.data;
    } else {
        return [];
    }
};

export const updatePersonalityTeachingData = async (alias, data) => {
    const result = await api.put(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.teachingInfrastructure}`, data)

    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const getPersonalityAliasSuggestions = async (limit, page, inputValue) => {
    const formData = new FormData();
    formData.append('page', page);
    formData.append('limit', limit);

    if (inputValue) {
        formData.append('search', inputValue); // Add search term
    }

    const params = new URLSearchParams(formData);
    const result = await api.get(`${APIS.BASE_URL}${APIS.personalitylistAliasSuggestions}`, {
        params: params
    })

    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

// Shared helper for TypeaheadWidget: returns [{ label, value }] options
export const fetchPersonalityAliasSuggestions = async (
    inputValue,
    { limit = 20, page = 1, onError } = {}
) => {
    const search = typeof inputValue === 'string' ? inputValue.trim() : '';
    if (!search) return [];

    try {
        const result = await getPersonalityAliasSuggestions(limit, page, search);
        const items = result?.data?.data || [];

        return items
            .map((item) => {
                const alias = typeof item === 'string' ? item : item?.alias;
                return {
                    label: alias,
                    value: alias
                };
            })
            .filter((opt) => opt?.value);
    } catch (e) {
        if (typeof onError === 'function') onError(e);
        return [];
    }
};

export const getPersonalityDetailsByAlias = async (alias) => {
    try {
        const result = await api.get(`${APIS.BASE_URL}admin/personality/${alias}/${APIS.filmBasic}`);
        if (result) {
            return result?.data?.data;
        } else {
            return null;
        }
    } catch (error) {
        console.error('Error fetching personality details:', error);
        return null;
    }
};

// Exhibitions API functions
export const getPersonalityExhibitionsData = async (alias) => {
    const result = await api.get(`${APIS.BASE_URL}admin/personality/${alias}/bodyofwork/exhibitions`);
    if (result) {
        return result?.data?.data;
    } else {
        return [];
    }
};

export const updatePersonalityExhibitionsData = async (alias, data) => {
    const result = await api.put(`${APIS.BASE_URL}admin/personality/${alias}/bodyofwork/exhibitions`, data);
    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const deletePersonalityExhibitionsData = async (alias, deletedItems) => {
    const result = await api.delete(`${APIS.BASE_URL}admin/personality/${alias}/bodyofwork/exhibitions`, {data: { deletedItems }});
    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

// Brand Ambassador API functions
export const getPersonalityBrandAmbassadorData = async (alias) => {
    const result = await api.get(`${APIS.BASE_URL}admin/personality/${alias}/economics/brand-ambassadorship`);
    if (result) {
        return result?.data?.data?.data;
    } else {
        return [];
    }
};

export const updatePersonalityBrandAmbassadorData = async (alias, data) => {
    const result = await api.put(`${APIS.BASE_URL}admin/personality/${alias}/economics/brand-ambassadorship`, data);
    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const deletePersonalityBrandAmbassadorData = async (alias, data) => {
    const result = await api.delete(`${APIS.BASE_URL}admin/personality/${alias}/economics/brand-ambassadorship`, { data });
    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

// Top Auction Sales API functions
export const getPersonalityTopAuctionSalesData = async (alias) => {
    const result = await api.get(`${APIS.BASE_URL}admin/personality/${alias}/economics/auction-sales`);
    if (result) {
        return result?.data?.data;
    } else {
        return [];
    }
};

export const updatePersonalityTopAuctionSalesData = async (alias, data) => {
    const result = await api.put(`${APIS.BASE_URL}admin/personality/${alias}/economics/auction-sales`, data);
    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const deletePersonalityTopAuctionSalesData = async (alias, data) => {
    const result = await api.delete(`${APIS.BASE_URL}admin/personality/${alias}/economics/auction-sales`, { data });
    if (result) {
        return result?.data;
    } else {
        return [];
    }
};

export const getInstitutionAliasSuggestions = async (limit, page, inputValue) => {
    const formData = new FormData();
    formData.append('page', page);
    formData.append('limit', limit);

    if (inputValue) {
        formData.append('search', inputValue); // Add search term
    }

    const params = new URLSearchParams(formData);
    const result = await api.get(`${APIS.BASE_URL}${APIS.institutionlistAliasSuggestions}`, {
        params: params
    })

    if (result) {
        return result?.data;
    } else {
        return [];
    }
};